import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule, NgClass } from '@angular/common';
import { ProfileComponent } from '../profile/profile.component';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { AuthorizationService } from '../../services/authorization.service';
import { Permissions } from '../../shared/permissions';
import { RoutePaths } from '../../shared/types/common';
import { StateService } from '../../services/state.service';
import { ComingSoonComponent } from '../../shared/components/coming-soon/coming-soon.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AssetVersionPipe } from '../../pipes/asset-version.pipe';
import { tooltips } from '../../utils/tooltips';
import { PremiumTooltipComponent } from '../../shared/components/premium-tooltip/premium-tooltip.component';
import { TippyDirective } from '@ngneat/helipopper';
import { PosthogService } from '../../services/posthog.service';
import { PosthogEventName, PosthogEventCode } from '../../services/types';

interface RouteConfig {
  route: RoutePaths;
  icon: string;
  text: string;
  disable: boolean;
  comingSoon: boolean;
  premiumBadge: boolean;
}

@Component({
  selector: 'app-main-side-menu',
  imports: [
    NgClass,
    ProfileComponent,
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    ComingSoonComponent,
    AssetVersionPipe,
    CommonModule,
    TippyDirective,
    PremiumTooltipComponent
  ],
  templateUrl: './main-side-menu.component.html',
  styleUrl: './main-side-menu.component.scss'
})
export class MainSideMenuComponent implements OnInit {

  public isProfileOpen: boolean = false;
  public disabledTabs: Partial<Record<RoutePaths, boolean>> = {};
  public profilePositions = [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: 0 }] as ConnectedPosition[];
  public RoutePaths = RoutePaths;
  protected readonly tooltips = tooltips;
  protected readonly dataCatalogUrl = 'https://browsi.atlassian.net/wiki/spaces/IH/pages/3525869580/Data+Catalog?utm_source=app&utm_medium=side_menu';

  public signalsPageConfig = {route: RoutePaths.SIGNALS, icon: 'svg/signals-logo.svg', text: 'Signals'};
  public isCopilotHovered: boolean = false;
  public hoveredMap: Record<string, boolean> = {};

  private posthogService = inject(PosthogService);
  private destroy$ = inject(DestroyRef);
  public userGivenName: string = '';
  public userFamilyName: string = '';

  public firstSectionRouteConfigs: RouteConfig[] = [];
  public secondSectionRouteConfigs: RouteConfig[] = [];
  public thirdSectionRouteConfigs: RouteConfig[] = [];

  constructor(private router: Router, private authorizationService: AuthorizationService, private stateService: StateService) { }

  ngOnInit() {
    this.stateService.loading = true;
    this.authorizationService.userProfile$.pipe(
      takeUntilDestroyed(this.destroy$)
    ).subscribe(user => {
      this.userGivenName = user.firstName;
      this.userFamilyName = user.lastName;
    });
    this.authorizationService.permissions$.pipe(
      takeUntilDestroyed(this.destroy$)
    ).subscribe(permissions => {
      if (permissions.length > 0) {
        this.setDisabledTabs();
        this.setRouteConfigs();
        this.stateService.loading = false;
      }
    });
  }

  get currentRoute(): string {
    return this.router.url.split('?')[0];
  }

  isActiveRoute(route: RoutePaths): boolean {
    const parts = this.currentRoute.slice(1).split('/');

    const isAlertCenterRoute = this.currentRoute.startsWith('/' + RoutePaths.ALERT_CENTER) || this.currentRoute.startsWith('/' + RoutePaths.ALERT_VIEW);
    if (isAlertCenterRoute) {
      return route === RoutePaths.ALERT_CENTER;
    }

    return parts[0] === route;
  }

  private isDisabledRoute(route: RoutePaths): boolean {
    return this.disabledTabs[route] ?? false;
  }

  private getRouteExtraConfig(route: RoutePaths): { disable: boolean; comingSoon: boolean; premiumBadge: boolean; } {
    const isDisabledTab = this.disabledTabs[route] ?? false;
    const premiumBadge = (this.isTrialUser || this.isExpiredUser) && [RoutePaths.AI_PRESENCE, RoutePaths.AUDIENCE, RoutePaths.REPUTATION].includes(route);
    const comingSoon = this.isExpiredUser ? false :
      this.isTrialUser ? isDisabledTab && !premiumBadge :
      isDisabledTab;
    return {
      disable: isDisabledTab,
      comingSoon,
      premiumBadge,
    }
  }

  navigate(route: string): void {
    this.isProfileOpen = false;
    this.router.navigate([`/${route}`])
  }

  navigateIfEnabled(route: RoutePaths): void {
    if (route === RoutePaths.DATA_POINTS) {
      this.openDataCatalog();
      return;
    }
    if (!this.disabledTabs[route]) {
      this.navigate(route);
    }
  }

  openProfileScreen() {
    this.isProfileOpen = !this.isProfileOpen
  }

  openDataCatalog() {
    this.posthogService.capture(PosthogEventName.EXTERNAL_LINK_CLICKED, {
      [PosthogEventCode.CLICK_URL]: this.dataCatalogUrl,
      [PosthogEventCode.CLICK_TEXT]: 'Data Catalog'
    });
    window.open(this.dataCatalogUrl, '_blank');
  }

  setDisabledTabs() {
    this.disabledTabs[RoutePaths.MY_BRAND] = !this.authorizationService.hasPermissions([Permissions.READ_MY_BRAND_DASHBOARD]);
    this.disabledTabs[RoutePaths.COMPETITION] = !this.authorizationService.hasPermissions([Permissions.READ_MY_COMPETITION_DASHBOARD]);
    this.disabledTabs[RoutePaths.MESSAGING] = !this.authorizationService.hasPermissions([Permissions.READ_MESSAGING_SCREEN]);
    this.disabledTabs[RoutePaths.CAMPAIGNS] = !this.authorizationService.hasPermissions([Permissions.READ_MY_CAMPAIGNS_DASHBOARD]);
    this.disabledTabs[RoutePaths.POLARIS] = !this.authorizationService.hasPermissions([Permissions.READ_COPILOT_SCREEN]);
    this.disabledTabs[RoutePaths.AI_PRESENCE] = !this.authorizationService.hasPermissions([Permissions.READ_AI_PRESENCE_SCREEN]);
    this.disabledTabs[RoutePaths.ALERT_CENTER] = !this.authorizationService.hasPermissions([Permissions.READ_POLARIS_ALERTS, Permissions.WRITE_POLARIS_ALERTS]);
    this.disabledTabs[RoutePaths.DATA_POINTS] = !this.authorizationService.hasPermissions([Permissions.READ_DATAPOINT_DOC]);
    this.disabledTabs[RoutePaths.AUDIENCE] = true;
    this.disabledTabs[RoutePaths.REPUTATION] = true;
  }

  setRouteConfigs() {
    this.firstSectionRouteConfigs = [
      {
        route: RoutePaths.MY_BRAND,
        icon: this.isDisabledRoute(RoutePaths.MY_BRAND) ? 'svg/my-brand-tab-disabled.svg' : 'svg/my-brand-tab.svg',
        text: 'My Brand',
        ...this.getRouteExtraConfig(RoutePaths.MY_BRAND)
      },
      {
        route: RoutePaths.COMPETITION,
        icon: this.isDisabledRoute(RoutePaths.COMPETITION) ? 'svg/my-competition-tab-disabled.svg' : 'svg/my-competition-tab.svg',
        text: 'My Competition',
        ...this.getRouteExtraConfig(RoutePaths.COMPETITION)
      },
      {
        route: RoutePaths.MESSAGING,
        icon: this.isDisabledRoute(RoutePaths.MESSAGING) ? 'svg/messaging-tab-disabled.svg' : 'svg/messaging-tab.svg',
        text: 'Messaging',
        ...this.getRouteExtraConfig(RoutePaths.MESSAGING)
      },
      {
        route: RoutePaths.AI_PRESENCE,
        icon: this.isDisabledRoute(RoutePaths.AI_PRESENCE) ? 'svg/ai-presence-gray.svg' : 'svg/ai-presence.svg',
        text: 'AI Presence',
        ...this.getRouteExtraConfig(RoutePaths.AI_PRESENCE)
      },
      {
        route: RoutePaths.AUDIENCE,
        icon: this.isDisabledRoute(RoutePaths.AUDIENCE) ? 'svg/audience-gray.svg' : '',
        text: 'Audience',
        ...this.getRouteExtraConfig(RoutePaths.AUDIENCE)
      },
      { route: RoutePaths.REPUTATION,
        icon: this.isDisabledRoute(RoutePaths.REPUTATION) ? 'svg/flower-tulip-gray.svg' : '',
        text: 'Reputation',
        ...this.getRouteExtraConfig(RoutePaths.REPUTATION)
      },
    ]
    this.secondSectionRouteConfigs = [
      {
        route: RoutePaths.CAMPAIGNS,
        icon: this.isDisabledRoute(RoutePaths.CAMPAIGNS) ? 'svg/my-campaigns-tab-disabled.svg' : 'svg/my-campaigns-tab.svg',
        text: 'My Campaigns',
        ...this.getRouteExtraConfig(RoutePaths.CAMPAIGNS)
      },
    ]
    this.thirdSectionRouteConfigs = [
      {
        route: RoutePaths.ALERT_CENTER,
        icon: this.isDisabledRoute(RoutePaths.ALERT_CENTER) ? 'svg/alerts-tab-disabled.svg' : 'svg/alerts-tab.svg',
        text: 'Alert Center',
        ...this.getRouteExtraConfig(RoutePaths.ALERT_CENTER)
      },
      {
        route: RoutePaths.DATA_POINTS,
        icon: this.isDisabledRoute(RoutePaths.DATA_POINTS) ? 'svg/data-catalog-disabled.svg' : 'svg/data-catalog.svg',
        text: 'Data Catalog',
        ...this.getRouteExtraConfig(RoutePaths.DATA_POINTS)
      },
    ]
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.isProfileOpen = false;
    }
  }

  get isTrialUser(): boolean {
    return this.authorizationService.isTrialUser;
  }

  get isExpiredUser(): boolean {
    return this.authorizationService.isExpiredUser;
  }

  get shouldDisplaySignals(): boolean {
    return this.authorizationService.hasPermissions([Permissions.READ_SIGNAL]);
  }
}
